/**
 * Базовый режим работы с полисом.
 *
 * Все режимы (оформление / переоформление / пролонгация / …) должны наследоваться
 * от BaseMode и переопределять только те методы, в которых они отличаются от
 * поведения по умолчанию (== оформление с нуля).
 *
 * Разграничение зон ответственности (важно!):
 *
 *  1. Модуль продукта (BpmOsgpoModule.Client.dataFieldsSettings и т.п.)
 *     отвечает за ПОЛЯ ФОРМЫ: какие поля существуют, какие обязательны,
 *     видимы и заблокированы в зависимости от СТРУКТУРЫ ДАННЫХ
 *     (резидент/ФЛ/заполненность документа и т.д.).
 *
 *  2. Режим отвечает за БИЗНЕС-СЦЕНАРИЙ:
 *     - действия над полисом (удалить страхователя, поменять местами, сохранить
 *       черновик и т.д.) — через `canXxx()` / lifecycle-хуки ниже;
 *     - трактовка результата калькуляции (`hasPremium`) — в т.ч. нулевая премия;
 *     - ограничения полей формы поверх базовых настроек модуля — через
 *       ЕДИНУЮ точку `decorateFields(scope, settings, ctx)`.
 *
 *  Благодаря этому правило «ИИН страхователя нельзя менять в переоформлении»
 *  живёт в одном месте (режиме), а форма потребляет уже готовый объект
 *  {required, show, disabled} и ничего не знает про бизнес-сценарий.
 */
export default class BaseMode {
    static code  = 'create'
    static title = 'Оформление'
    static calcTitle = 'Расчитать'
    static checkoutTitle = 'Оформить'

    /** POST: расчёт премии ОГПО (V2 / KBS) */
    static calculateUrl = '/api/v2/calculate/ogpo/calculate-premium'
    /** POST: запуск оформления договора BPM */
    static checkoutUrl = '/api/v2/contract/bpm/checkout'

    // ---------- Действия над страхователем ----------
    /** Можно ли удалить страхователя. */
    static canDeleteInsurer() { return true }
    /** Можно ли поменять страхователя местами с застрахованным. */
    static canSwitchInsurer() { return true }
    /** Можно ли редактировать страхователя. */
    static canEditInsurer() { return true }
    /** Можно ли добавить страхователя. */
    static canAddInsurer() { return true }

    // ---------- Действия над застрахованными ----------
    /** Можно ли удалить застрахованного. */
    static canDeleteInsured() { return true }
    /** Можно ли редактировать застрахованного. */
    static canEditInsured() { return true }
    /** Можно ли добавить застрахованного. */
    static canAddInsured() { return true }

    // ---------- Действия над ТС ----------
    /** Можно ли удалить ТС. */
    static canDeleteVehicle() { return true }
    /** Можно ли редактировать ТС. */
    static canEditVehicle() { return true }
    /** Можно ли добавить ТС. */
    static canAddVehicle() { return true }

    // ---------- Калькуляция ----------
    /**
     * Премия считается заданной после расчёта.
     * Режим может переопределить (например, допустить нулевую премию).
     * @param {Object} policy
     * @returns {boolean}
     */
    static hasPremium(policy) {
        return !!policy?.premium
    }

    // ---------- Lifecycle ----------
    /**
     * Сохранять ли черновик для этого режима.
     * Например, переоформление черновики не ведёт.
     */
    // eslint-disable-next-line no-unused-vars
    static shouldSaveDraft(policy) { return true }

    /**
     * Первичная инициализация полиса при монтировании компонента,
     * когда в URL нет `draft`. Каждый режим решает сам, откуда брать данные.
     *
     * По умолчанию — просто дотягиваем справочник менеджеров (поведение
     * «чистого» оформления).
     *
     * @param {Object} component — Vue-компонент продукта (`this`).
     */
    static loadInitialPolicy(component) {
        if (component && typeof component.getManagers === 'function') {
            component.getManagers()
        }
    }

    /**
     * Дата начала договора по умолчанию для текущего режима.
     * Может быть переопределена в наследниках (rewrite/prolong/...).
     */
    static getTreatyStartDate(component) {
        return component?.tomorrow ?? null
    }

    /**
     * Минимально допустимая дата начала договора для текущего режима.
     * Может быть переопределена в наследниках.
     */
    static getTreatyMinDate(component) {
        return component?.tomorrow ?? null
    }

    /**
     * Справочник доступных сроков договора (месяцы).
     * Формат совместим с SelectField: { value: label }.
     */
    static getTreatyTermsDictionary() {
        return {
            12: '12',
        }
    }

    // ---------- Декоратор полей формы ----------
    /**
     * Единственная точка, через которую режим может повлиять на базовые
     * настройки полей формы, которые строит модуль продукта.
     *
     * Реализации ДОЛЖНЫ:
     *  - возвращать НОВЫЙ объект (исходный не мутируется);
     *  - менять только те поля, что реально отличаются в данном режиме;
     *  - не полагаться на наличие какого-то конкретного поля (settings?.iin &&).
     *
     * Типовые значения scope (могут расширяться по мере надобности):
     *   'clientData'   — данные клиента (основная форма, CustomerBpmData)
     *   'clientSearch' — форма поиска клиента (CustomerBpmSearch)
     *   'clientFilter' — фильтры ФЛ/Резидент (CustomerBpmSearch)
     *   'vehicleData'  — данные ТС (VehicleBpmData)
     *
     * @param {string} scope
     * @param {Object} settings — { field: {required, show, disabled} }
     * @param {Object} ctx — { clientType?, policy?, ... }
     * @returns {Object} новый settings
     */
    // eslint-disable-next-line no-unused-vars
    static decorateFields(scope, settings, ctx) {
        return settings
    }
}
